import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, Observable, of, shareReplay } from 'rxjs';

interface VersionResponse {
  version: string;
  commitSha?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SystemService {
  private readonly url = '/api';
  private readonly http: HttpClient = inject(HttpClient);

  private readonly versionResponse$ = this.http.get<VersionResponse>(`${this.url}/version`).pipe(shareReplay(1));

  /**
   * A signal that holds the version of the system, fetched from the backend.
   * It is initialized with '0.0.0' until the actual version is fetched.
   */
  readonly version = toSignal(this.versionResponse$.pipe(map((r) => r.version)), { initialValue: '0.0.0' });

  /**
   * A signal that holds the commit SHA of the system, fetched from the backend.
   * It is initialized with an empty string until the actual commit SHA is fetched.
   */
  readonly commitSha = toSignal(this.versionResponse$.pipe(map((r) => r.commitSha ?? '')), { initialValue: '' });

  /**
   * Fetches the version of the system from the backend.
   * @deprecated This method is deprecated. Use the `version` signal instead.
   * @returns Version of the system.
   */
  getVersion(): Observable<string> {
    return this.http.get<VersionResponse>(`${this.url}/version`).pipe(map((response) => response.version));
  }

  getCopyrightYear(): Observable<string> {
    const fullYear = new Date().getFullYear();
    return of(String(fullYear));
  }
}
